/**
 * Stand der Anfragen am Projekt (Migration 175).
 *
 * - `standVon` / `StandChip`: je Material in einem Wort, wo es steht —
 *   nicht angefragt, angefragt, Angebot(e), gewählt, bestellt, geliefert.
 * - `StandPanel`: der Preisvergleich — je angefragtem Lieferanten Preis und
 *   Lieferzeit eintragen, eines „nehmen" (dann liefert er das Material im
 *   Projekt), dazu bestellt/geliefert von Hand.
 * - `Wiedervorlagen`: über der Materialliste, wenn eine Antwort aussteht
 *   (nach 3 Werktagen) oder eine Antwort da ist, deren Preise noch fehlen.
 *   Etappe 8: der Evo liest die Antwort; Preise, Lieferzeiten und Absagen stehen
 *   als Vorschlag da — „Übernehmen" trägt sie ein und pflegt so den Katalog.
 */
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { createPortal } from "react-dom";

import { fehlerText } from "@vimaana/ui";

import {
  useAnfrageStand, useProjektMaterial, useProjektMaterialAendern,
  type AnfrageKopf, type BestellStatus, type MaterialAnfrageStand, type ProjektMaterialDaten, type ProjektMaterialZeile,
} from "../../hooks/useLieferanten";
import PostSchreiben from "./PostSchreiben";
import "../../styles/anfrage.css";

const eur = (n: number | null) => (n == null ? "" : n.toLocaleString("de-DE", { style: "currency", currency: "EUR" }));
const tag = (iso: string) => (iso ? new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" }) : "");

export type StandArt = "offen" | "angefragt" | "angebot" | "gewaehlt" | "bestellt" | "geliefert";

export function standVon(m: ProjektMaterialZeile): { art: StandArt; text: string } {
  /* Laut Lieferschein: Menge und jüngstes Lieferdatum („geliefert 17,4 t · 02.10.") —
     sticht das von Hand gesetzte „geliefert", weil es mehr sagt. */
  if (m.geliefert_am) return { art: "geliefert", text: ["geliefert", m.geliefert, "·", tag(m.geliefert_am)].filter(Boolean).join(" ") };
  if (m.bestell_status === "geliefert") return { art: "geliefert", text: "geliefert" };
  if (m.bestell_status === "bestellt") return { art: "bestellt", text: "bestellt" };
  const g = m.anfragen.find((a) => a.status === "gewaehlt");
  if (g) return { art: "gewaehlt", text: `Angebot ${g.lieferant_name}${g.preis_netto != null ? ` · ${eur(g.preis_netto)}` : ""} gewählt` };
  const angebote = m.anfragen.filter((a) => a.status === "angebot");
  if (angebote.length === 1) {
    const a = angebote[0];
    return { art: "angebot", text: `Angebot ${a.lieferant_name}${a.preis_netto != null ? ` · ${eur(a.preis_netto)}` : ""}` };
  }
  if (angebote.length > 1) {
    const preise = angebote.map((a) => a.preis_netto).filter((p): p is number => p != null);
    return { art: "angebot", text: `${angebote.length} Angebote${preise.length ? ` · ab ${eur(Math.min(...preise))}` : ""}` };
  }
  const offen = m.anfragen.filter((a) => a.status === "angefragt");
  if (offen.length) {
    const antwort = offen.some((a) => a.antwort);
    return { art: "angefragt", text: `angefragt bei ${offen.map((a) => a.lieferant_name).join(", ")} · ${tag(offen[0].gesendet_am)}${antwort ? " · Antwort da" : ""}` };
  }
  if (m.anfragen.length && m.anfragen.every((a) => a.status === "abgesagt")) return { art: "offen", text: "abgesagt — neu anfragen" };
  return { art: "offen", text: "nicht angefragt" };
}

export function StandChip({ m, offen, onClick }: { m: ProjektMaterialZeile; offen: boolean; onClick: () => void }) {
  const s = standVon(m);
  return (
    <button type="button" className={"an-stand-chip ist-" + s.art + (offen ? " ist-aufgeklappt" : "")} aria-expanded={offen} onClick={onClick}
      title="Stand, Angebote vergleichen, bestellt/geliefert">
      {s.text}
    </button>
  );
}

// ── Vergleich und Bestellstand ───────────────────────────────────────────

export function StandPanel({ m, projektId, onAnfrage }: { m: ProjektMaterialZeile; projektId: string; onAnfrage: () => void }) {
  const st = useAnfrageStand(projektId);
  const a = useProjektMaterialAendern(projektId);
  const zeilen = m.anfragen;
  const preise = zeilen.filter((z) => z.status !== "abgesagt" && z.preis_netto != null).map((z) => z.preis_netto as number);
  const billigster = preise.length > 1 ? Math.min(...preise) : null;
  const fehler = st.position.error || st.waehlen.error || a.aendern.error;
  return (
    <div className="an-stand">
      {zeilen.length === 0 ? (
        <div className="an-stand-leer">
          <span>Noch nicht angefragt.</span>
          <button type="button" className="lf-knopf is-primaer" onClick={onAnfrage}>Anfrage schreiben</button>
        </div>
      ) : (
        <>
          <ul className="an-vergleich" aria-label={`Angebote für ${m.name}`}>
            {zeilen.map((z) => (
              <VergleichZeile key={z.anfrage_id} z={z} pmId={m.id} einheit={m.einheit} billigster={billigster} projektId={projektId} />
            ))}
          </ul>
          <div className="an-stand-fuss">
            <button type="button" className="lf-knopf" onClick={onAnfrage}>＋ Weitere anfragen</button>
          </div>
        </>
      )}
      <div className="an-bestellt" role="group" aria-label="Bestellstand">
        <span className="lf-label-klein">Bestellung</span>
        {(["", "bestellt", "geliefert"] as BestellStatus[]).map((s) => (
          <button key={s || "offen"} type="button" aria-pressed={m.bestell_status === s} disabled={a.aendern.isPending}
            className={"vg-chip-knopf" + (m.bestell_status === s ? " is-active" : "")}
            onClick={() => a.aendern.mutate({ id: m.id, bestell_status: s })}>
            {s === "" ? "offen" : s === "bestellt" ? "bestellt" : "geliefert"}
          </button>
        ))}
        {m.geliefert_am && <span className="lf-hinweis an-lieferschein">Lieferschein vom {new Date(m.geliefert_am).toLocaleDateString("de-DE")} im Projekt</span>}
      </div>
      {fehler && <div className="lf-fehler">{fehlerText(fehler)}</div>}
    </div>
  );
}

const STATUS_TEXT: Record<MaterialAnfrageStand["status"], string> = {
  angefragt: "angefragt", angebot: "Angebot", gewaehlt: "genommen", nicht_gewaehlt: "nicht genommen", abgesagt: "kann nicht liefern",
};

function VergleichZeile({ z, pmId, einheit, billigster, projektId }: {
  z: MaterialAnfrageStand; pmId: string; einheit: string; billigster: number | null; projektId: string;
}) {
  const st = useAnfrageStand(projektId);
  const [preis, setPreis] = useState(z.preis_netto == null ? "" : String(z.preis_netto).replace(".", ","));
  const [lieferzeit, setLieferzeit] = useState(z.lieferzeit);
  const preisZahl = preis.trim() ? Number(preis.replace(/\./g, "").replace(",", ".")) : null;
  const preisOk = preisZahl === null || (Number.isFinite(preisZahl) && preisZahl >= 0);
  const speicherePreis = () => {
    if (!preisOk || preisZahl === z.preis_netto) return;
    st.position.mutate(preisZahl === null
      ? { anfrageId: z.anfrage_id, pmId, preis_leeren: true }
      : { anfrageId: z.anfrage_id, pmId, preis_netto: preisZahl });
  };
  const aus = z.status === "abgesagt" || z.status === "nicht_gewaehlt";
  return (
    <li className={"an-vzeile ist-" + z.status + (billigster != null && z.preis_netto === billigster ? " ist-guenstig" : "")}>
      <div className="an-vzeile-kopf">
        <span className="an-vzeile-name">{z.lieferant_name}</span>
        <span className="an-vzeile-info">
          {STATUS_TEXT[z.status]} · angefragt {tag(z.gesendet_am)}{z.menge ? ` · ${[z.menge, z.einheit].filter(Boolean).join(" ")}` : ""}
          {z.antwort && z.status === "angefragt" ? " · Antwort da" : ""}
          {billigster != null && z.preis_netto === billigster ? " · günstigstes" : ""}
        </span>
      </div>
      <div className="an-vzeile-felder">
        <label className="lf-label">Preis netto{einheit ? ` je ${einheit}` : ""}
          <input className="lf-feld" inputMode="decimal" value={preis} placeholder="z. B. 12,50" disabled={z.status === "abgesagt"}
            onChange={(e) => setPreis(e.target.value)} onBlur={speicherePreis}
            onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }} />
        </label>
        <label className="lf-label">Lieferzeit
          <input className="lf-feld" value={lieferzeit} placeholder="z. B. 3 Tage, KW 42" disabled={z.status === "abgesagt"}
            onChange={(e) => setLieferzeit(e.target.value)}
            onBlur={() => { if (lieferzeit !== z.lieferzeit) st.position.mutate({ anfrageId: z.anfrage_id, pmId, lieferzeit }); }}
            onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }} />
        </label>
        <div className="an-vzeile-tat">
          {z.status === "gewaehlt" ? (
            <span className="an-genommen">✓ genommen</span>
          ) : (
            <button type="button" className={"lf-knopf" + (z.status === "angebot" ? " is-primaer" : "")}
              disabled={st.waehlen.isPending || z.status === "abgesagt"}
              title="Dieser Lieferant liefert das Material in diesem Projekt"
              onClick={() => st.waehlen.mutate({ anfrageId: z.anfrage_id, pmId })}>Nehmen</button>
          )}
          {z.status === "abgesagt" ? (
            <button type="button" className="lf-knopf" onClick={() => st.position.mutate({ anfrageId: z.anfrage_id, pmId, status: z.preis_netto != null ? "angebot" : "angefragt" })}>Doch lieferbar</button>
          ) : !aus && (
            <button type="button" className="lf-knopf" onClick={() => st.position.mutate({ anfrageId: z.anfrage_id, pmId, status: "abgesagt" })}>Kann nicht</button>
          )}
        </div>
      </div>
      {!preisOk && <div className="lf-fehler">Der Preis ist keine Zahl.</div>}
    </li>
  );
}

// ── Wiedervorlage ────────────────────────────────────────────────────────

/** Im Kopf der (zugeklappten) Sektion auf der Projektseite: was auf einen wartet. */
export function MaterialKopf({ projektId }: { projektId: string }) {
  const q = useProjektMaterial(projektId);
  const d = q.data;
  if (!d) return null;
  const antwort = d.anfragen.filter((a) => a.offen && a.antwort).length;
  const faellig = d.anfragen.filter((a) => a.faellig).length;
  if (!antwort && !faellig) return d.materialien.length ? <>{d.materialien.length} Material</> : null;
  return (
    <>
      {antwort > 0 && <span className="an-kopf-marke ist-antwort">{antwort === 1 ? "Antwort da" : `${antwort} Antworten da`}</span>}
      {faellig > 0 && <span className="an-kopf-marke ist-faellig">{faellig === 1 ? "Antwort ausstehend" : `${faellig} Antworten ausstehend`}</span>}
    </>
  );
}

/** Oben in der Materialliste: ausstehende Antworten und Antworten ohne Preise. */
export function Wiedervorlagen({ d, projektId, onStand }: { d: ProjektMaterialDaten; projektId: string; onStand: (pmId: string) => void }) {
  const st = useAnfrageStand(projektId);
  const qc = useQueryClient();
  const [nachfassen, setNachfassen] = useState<AnfrageKopf | null>(null);
  const faellig = d.anfragen.filter((a) => a.faellig);
  const antwortDa = d.anfragen.filter((a) => a.offen && a.antwort);
  const liest = antwortDa.some((a) => a.gelesen?.status === "liest");
  // Solange der Evo liest, alle paar Sekunden nachsehen — sonst nicht.
  useEffect(() => {
    if (!liest) return;
    const t = window.setInterval(() => qc.invalidateQueries({ queryKey: ["projekt-material", projektId] }), 8000);
    return () => window.clearInterval(t);
  }, [liest, projektId, qc]);
  if (!faellig.length && !antwortDa.length) return null;
  const namen = (a: AnfrageKopf) => a.pm_ids.map((id) => d.materialien.find((m) => m.id === id)?.name).filter(Boolean) as string[];
  return (
    <div className="an-wv">
      {antwortDa.map((a) => (
        <div key={a.id} className="an-wv-zeile ist-antwort">
          <div className="an-wv-text">
            <strong>Antwort von {a.lieferant_name}</strong> auf die Anfrage vom {tag(a.gesendet_am)} ist da
            {a.antwort ? ` (${tag(a.antwort.ts)})` : ""} — steht im Schriftverkehr.
            {a.gelesen?.status === "liest" ? " Clara liest die Antwort …" : a.gelesen?.status === "fertig" ? "" : ` Preise eintragen: ${namen(a).join(", ")}.`}
          </div>
          {a.gelesen?.status === "fertig" && <Gelesen a={a} d={d} st={st} />}
          <div className="an-wv-tat">
            {!a.gelesen && a.antwort && (
              <button type="button" className="lf-knopf is-primaer" disabled={st.lesen.isPending}
                title="Clara liest Preise, Lieferzeiten und Absagen aus der Mail — du bestätigst"
                onClick={() => st.lesen.mutate({ anfrageId: a.id, mailId: a.antwort!.id })}>Clara die Preise lesen lassen</button>
            )}
            <button type="button" className="lf-knopf" onClick={() => a.pm_ids[0] && onStand(a.pm_ids[0])}>Von Hand eintragen</button>
            <button type="button" className="lf-knopf" disabled={st.erledigt.isPending} onClick={() => st.erledigt.mutate({ anfrageId: a.id, erledigt: true })}>Erledigt</button>
          </div>
        </div>
      ))}
      {faellig.map((a) => (
        <div key={a.id} className="an-wv-zeile ist-faellig">
          <div className="an-wv-text">
            <strong>Keine Antwort von {a.lieferant_name}</strong> — angefragt am {tag(a.gesendet_am)}{a.nachgefasst_am ? `, nachgefragt am ${tag(a.nachgefasst_am)}` : ""}: {namen(a).join(", ")}.
          </div>
          <div className="an-wv-tat">
            <button type="button" className="lf-knopf is-primaer" onClick={() => setNachfassen(a)}>Nachfragen</button>
            <button type="button" className="lf-knopf" disabled={st.erledigt.isPending} onClick={() => st.erledigt.mutate({ anfrageId: a.id, erledigt: true })}
              title="Z. B. am Telefon geklärt — keine Erinnerung mehr">Erledigt</button>
          </div>
        </div>
      ))}
      {(st.erledigt.error || st.gelesen.error || st.lesen.error) && (
        <div className="lf-fehler">{fehlerText(st.erledigt.error || st.gelesen.error || st.lesen.error)}</div>
      )}
      {nachfassen && createPortal(
        <PostSchreiben
          onClose={() => setNachfassen(null)}
          projektId={projektId}
          lieferantId={nachfassen.lieferant_id}
          ziele={[]}
          an={nachfassen.an}
          antwortAuf={nachfassen.message_id || undefined}
          nachfassen={nachfassen.id}
          betreff={`Nachfrage: Anfrage – ${d.projekt.title} (Kommission ${d.kommission})`}
          text={[
            "Guten Tag,",
            "",
            `am ${new Date(nachfassen.gesendet_am).toLocaleDateString("de-DE")} hatten wir Sie um ein Angebot gebeten für: ${namen(nachfassen).join(", ")} (Kommission ${d.kommission}).`,
            "Könnten Sie uns kurz Verfügbarkeit, Preis und Liefertermin nennen?",
            "",
            "Vielen Dank!",
          ].join("\n")}
          titel={`Nachfrage an ${nachfassen.lieferant_name}`}
        />,
        document.body,
      )}
    </div>
  );
}

/** Was der Evo aus der Antwort gelesen hat — erst das Ja trägt es ein (Etappe 8). */
function Gelesen({ a, d, st }: { a: AnfrageKopf; d: ProjektMaterialDaten; st: ReturnType<typeof useAnfrageStand> }) {
  const g = a.gelesen!;
  const zeilen = g.positionen || [];
  const name = (id: string) => d.materialien.find((m) => m.id === id)?.name || "Material";
  const mitPreis = zeilen.filter((z) => z.preis_netto != null).length;
  return (
    <div className="an-gelesen">
      <p className="an-gelesen-titel">
        Aus der Mail gelesen{zeilen.length === 0 ? ": keine Preise gefunden" : ""}
        {g.angebot_im_anhang && " · Preise stehen laut Mail im Anhang — dort nachsehen"}
      </p>
      {zeilen.length > 0 && (
        <ul className="an-gelesen-liste">
          {zeilen.map((z) => (
            <li key={z.pm_id} className={z.absage ? "ist-absage" : undefined}>
              <span className="an-gelesen-name">{name(z.pm_id)}</span>
              <span className="an-gelesen-wert">
                {z.absage ? "kann nicht liefern" : [
                  z.preis_netto != null ? `${eur(z.preis_netto)}${z.einheit ? ` / ${z.einheit}` : ""} netto` : "",
                  z.lieferzeit, z.notiz,
                ].filter(Boolean).join(" · ") || "—"}
              </span>
            </li>
          ))}
        </ul>
      )}
      <div className="an-wv-tat">
        {zeilen.length > 0 && (
          <button type="button" className="lf-knopf is-primaer" disabled={st.gelesen.isPending}
            title={mitPreis ? `Trägt ${mitPreis === 1 ? "den Preis" : `${mitPreis} Preise`} ein — auch in die Materialliste des Lieferanten` : undefined}
            onClick={() => st.gelesen.mutate({ anfrageId: a.id, auftragId: g.auftrag_id, annehmen: true })}>
            Übernehmen
          </button>
        )}
        <button type="button" className="lf-knopf" disabled={st.gelesen.isPending}
          onClick={() => st.gelesen.mutate({ anfrageId: a.id, auftragId: g.auftrag_id, annehmen: false })}>Verwerfen</button>
      </div>
    </div>
  );
}
